import { $generateHtmlFromNodes, $generateNodesFromDOM } from '@lexical/html'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { useEffect, useRef } from 'react'
import { $createParagraphNode, $getRoot, type LexicalEditor } from 'lexical'

const EXTERNAL_HTML_SYNC_TAG = 'external-html-sync'

const isStaleExternalHtml = (
  incoming: string,
  lastEmitted: string | null,
): boolean => {
  if (lastEmitted === null) {
    return false
  }
  if (incoming === lastEmitted) {
    return true
  }
  const lastHadLinks = lastEmitted.includes('<a ')
  const incomingHasLinks = incoming.includes('<a ')
  if (lastHadLinks && !incomingHasLinks) {
    return true
  }
  return false
}
const $loadHtmlIntoRoot = (editor: LexicalEditor, html: string): void => {
  const root = $getRoot()
  root.clear()

  const source = html.trim() ? html : '<p></p>'
  const dom = new DOMParser().parseFromString(
    `<!DOCTYPE html><html><body>${source}</body></html>`,
    'text/html',
  )
  const nodes = $generateNodesFromDOM(editor, dom.body)
  if (nodes.length === 0) {
    root.append($createParagraphNode())
  } else {
    root.append(...nodes)
  }
}

type HtmlSyncPluginProps = {
  value: string
  setValue: (html: string) => void
}

export const HtmlSyncPlugin = ({
  value,
  setValue,
}: HtmlSyncPluginProps): null => {
  const [editor] = useLexicalComposerContext()
  const lastEmittedRef = useRef<string | null>(null)
  const setValueRef = useRef(setValue)
  setValueRef.current = setValue

  useEffect(() => {
    return editor.registerUpdateListener(({ tags }) => {
      if (tags.has(EXTERNAL_HTML_SYNC_TAG)) {
        return
      }
      editor.getEditorState().read(() => {
        const html = $generateHtmlFromNodes(editor, null)
        if (html === lastEmittedRef.current) {
          return
        }
        lastEmittedRef.current = html
        setValueRef.current(html)
      })
    })
  }, [editor])

  useEffect(() => {
    if (isStaleExternalHtml(value, lastEmittedRef.current)) {
      return
    }

    const root = editor.getRootElement()
    if (
      root !== null &&
      document.activeElement === root &&
      lastEmittedRef.current !== null &&
      value !== lastEmittedRef.current
    ) {
      return
    }

    editor.update(
      () => {
        $loadHtmlIntoRoot(editor, value)
      },
      {
        tag: EXTERNAL_HTML_SYNC_TAG,
        onUpdate: () => {
          lastEmittedRef.current = value
        },
      },
    )
  }, [editor, value])

  return null
}
